import { supabase } from '@/lib/supabase';
import {
  Accessory,
  AccessoryInsert,
  AccessoryUpdate,
  PaginatedResult,
} from '@/types/models';
import { PAGE_SIZE } from '@/lib/constants';

export interface FetchAccessoriesParams {
  search?: string;
  category?: string;
  lowStockOnly?: boolean;
  archiveFilter?: 'active' | 'archived' | 'all';
  page?: number;
  isStaff?: boolean;
}

export async function fetchAccessories(
  params: FetchAccessoriesParams
): Promise<PaginatedResult<Accessory>> {
  const {
    search = '',
    category = '',
    lowStockOnly = false,
    archiveFilter = 'active',
    page = 0,
    isStaff = false,
  } = params;

  const from = page * PAGE_SIZE;
  const to = from + PAGE_SIZE - 1;

  if (isStaff) {
    const { data: rpcData, error: rpcErr } = await supabase.rpc('get_accessories_catalog');
    if (rpcErr) throw rpcErr;
    // Keep staff requests on the catalogue RPC even when it fails or is empty.
    if (archiveFilter === 'archived') return { data: [], count: 0 };
    let filtered = (rpcData as Accessory[]) || [];
    if (search.trim()) {
      const s = search.toLowerCase().trim();
      filtered = filtered.filter(
        (item) =>
          item.name?.toLowerCase().includes(s) ||
          item.sku?.toLowerCase().includes(s) ||
          item.category?.toLowerCase().includes(s)
      );
    }
    if (category) filtered = filtered.filter((i) => i.category === category);
    if (lowStockOnly) filtered = filtered.filter((i) => i.quantity <= i.reorder_level);

    const pageData = filtered.slice(from, from + PAGE_SIZE);
    return { data: pageData, count: filtered.length };
  }

  let query = supabase
    .from('accessories')
    .select('*, supplier:suppliers(id, name)', { count: 'exact' })
    .order('name', { ascending: true })
      .order('id', { ascending: true });

  if (archiveFilter === 'active') {
    query = query.is('archived_at', null);
  } else if (archiveFilter === 'archived') {
    query = query.not('archived_at', 'is', null);
  }

  if (search.trim()) {
    const s = search.trim();
    query = query.or(`name.ilike.%${s}%,sku.ilike.%${s}%,category.ilike.%${s}%`);
  }

  if (category) query = query.eq('category', category);

  if (lowStockOnly) {
    const { data, error } = await query;
    if (error) throw error;
    const filtered = (data ?? []).filter(
      (item: Accessory) => Number(item.quantity) <= Number(item.reorder_level)
    );
    const pageData = filtered.slice(from, to + 1);
    return { data: pageData as Accessory[], count: filtered.length };
  }

  query = query.range(from, to);

  const { data, error, count } = await query;
  if (error) throw error;

  return { data: (data ?? []) as Accessory[], count: count ?? 0 };
}

export async function fetchAccessoryById(id: string): Promise<Accessory> {
  const { data, error } = await supabase
    .from('accessories')
    .select('*, supplier:suppliers(id, name)')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as Accessory;
}

export async function createAccessory(payload: AccessoryInsert): Promise<Accessory> {
  const insertPayload = { ...payload };
  if (!insertPayload.sku || !insertPayload.sku.trim()) {
    delete insertPayload.sku;
  }

  const { data, error } = await supabase
    .from('accessories')
    .insert(insertPayload)
    .select('*, supplier:suppliers(id, name)')
    .single();

  if (error) throw error;
  return data as Accessory;
}

export async function updateAccessoryCatalogue(
  id: string,
  payload: AccessoryUpdate
): Promise<Accessory> {
  const { data, error } = await supabase
    .from('accessories')
    .update(payload)
    .eq('id', id)
    .select('*, supplier:suppliers(id, name)')
    .single();

  if (error) throw error;
  return data as Accessory;
}

export async function adjustAccessoryStock(params: {
  accessory_id: string;
  quantity_change: number;
  reason: string;
}): Promise<void> {
  if (!params.reason.trim()) {
    throw new Error('A reason is required for manual stock adjustments.');
  }

  const { error } = await supabase.rpc('adjust_accessory_stock', {
    p_accessory_id: params.accessory_id,
    p_quantity_change: params.quantity_change,
    p_reason: params.reason.trim(),
  });

  // Never repeat a stock mutation after an arbitrary server/network error.
  if (error) throw error;
}

export async function archiveAccessoryRpc(params: {
  accessoryId: string;
  reason: string;
}): Promise<{
  success: boolean;
  accessory_id: string;
  name: string;
  status: string;
  message: string;
}> {
  if (!params.reason.trim()) {
    throw new Error('A reason is required to archive an accessory.');
  }

  const { data, error } = await supabase.rpc('archive_accessory', {
    p_accessory_id: params.accessoryId,
    p_reason: params.reason.trim(),
  });

  if (error) throw error;
  return data as {
    success: boolean;
    accessory_id: string;
    name: string;
    status: string;
    message: string;
  };
}

export async function unarchiveAccessoryRpc(accessoryId: string): Promise<{
  success: boolean;
  accessory_id: string;
  name: string;
  status: string;
  message: string;
}> {
  const { data, error } = await supabase.rpc('unarchive_accessory', {
    p_accessory_id: accessoryId,
  });

  if (error) throw error;
  return data as {
    success: boolean;
    accessory_id: string;
    name: string;
    status: string;
    message: string;
  };
}

/**
 * Fetch all active unarchived accessories with bounded pagination (1000 rows/page).
 * Used for deterministic SKU matching and full selection in purchase forms.
 */
export async function fetchAllActiveAccessories(): Promise<Accessory[]> {
  const PAGE_SIZE = 1000;
  const all: Accessory[] = [];
  let from = 0;

  for (;;) {
    const { data, error } = await supabase
      .from('accessories')
      .select('*')
      .is('archived_at', null)
      .eq('is_active', true)
      .order('name', { ascending: true })
      .order('id', { ascending: true })
      .range(from, from + PAGE_SIZE - 1);

    if (error) throw error;
    const page = (data ?? []) as Accessory[];
    all.push(...page);
    if (page.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  return all;
}
